/**
 * widgets/DataTable.ts · 列表表组件 (28+ 列表页都用)
 *
 * v353 升级:
 *   - Column.tooltip: 列名 hover 显双语解释 (title attr)
 *   - Column.idShort: ID 缩展 (SO-2026-0027 → SO·0027 hover 看全 + 双击复制)
 *   - rowClick callback 加 currentTarget · 不污染冒泡
 *
 * v351 升级:
 *   - sticky thead (顶部固定)
 *   - 36px 行高 (Salesforce/SAP Fiori 行业标杆)
 *   - 移动端横向 scroll
 */

import { toast } from '@services/toast';

export interface Column<T = any> {
  key: string;
  label: string;
  width?: string;
  render?: (row: T) => string | HTMLElement;
  align?: 'left' | 'center' | 'right';
  /** v353 · 列名 tooltip (hover 显双语解释) */
  tooltip?: string;
  /** v353 · ID 缩展 (SO-2026-0027 → SO·0027 hover 看全 + 双击复制) */
  idShort?: boolean;
}

export interface DataTableOptions<T = any> {
  columns: Column<T>[];
  data: T[];
  emptyText?: string;
  rowClick?: (row: T) => void;
}

/**
 * v353 · ID 缩展 helper
 *   SO-2026-0027 → SO·0027
 *   PO-2026-00012 → PO·00012
 *   QUO-2026-0005 → QUO·0005
 *   WO-2026-0023-PRINT → WO·0023·PRINT
 */
export function shortenId(id: string): string {
  if (!id || typeof id !== 'string') return id || '';
  // 匹配 PREFIX-YYYY-NUMBER(-SUFFIX)* 格式
  const m = id.match(/^([A-Z]{2,5})-\d{4}-(\d+)(-.+)?$/);
  if (m) return `${m[1]}·${m[2]}${m[3] || ''}`;
  return id;
}

export function renderDataTable<T>(opts: DataTableOptions<T>): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'tn-data-table overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200';
  wrapper.style.maxHeight = 'calc(100vh - 200px)';
  wrapper.style.overflowY = 'auto';

  if (!opts.data.length) {
    wrapper.innerHTML = `
      <div class="text-center py-12 text-gray-400">
        <div class="text-4xl mb-2">📭</div>
        <div class="text-sm">${escapeHtml(opts.emptyText || '暂无数据 / Không có dữ liệu')}</div>
      </div>
    `;
    return wrapper;
  }

  const table = document.createElement('table');
  table.className = 'w-full text-sm';

  // thead (sticky · v353 加 tooltip)
  const thead = document.createElement('thead');
  thead.className = 'sticky top-0 z-10 bg-gray-50';
  thead.innerHTML = `
    <tr class="border-b border-gray-200">
      ${opts.columns.map(c => {
        const tooltipAttr = c.tooltip ? ` title="${escapeHtml(c.tooltip)}"` : '';
        const tooltipMark = c.tooltip ? ' <span style="color:#9ca3af;font-weight:normal;cursor:help">ⓘ</span>' : '';
        return `
          <th class="px-3 py-2 text-${c.align || 'left'} text-xs font-semibold text-gray-700 uppercase tracking-wide"
              ${c.width ? `style="width:${c.width}"` : ''}${tooltipAttr}>
            ${escapeHtml(c.label)}${tooltipMark}
          </th>`;
      }).join('')}
    </tr>
  `;
  table.appendChild(thead);

  // tbody (36px 行高)
  const tbody = document.createElement('tbody');
  for (const row of opts.data) {
    const tr = document.createElement('tr');
    tr.className = 'border-b border-gray-100 hover:bg-blue-50 transition-colors';
    tr.style.height = '36px';
    if (opts.rowClick) {
      tr.style.cursor = 'pointer';
      tr.addEventListener('click', (e) => {
        // 不拦截 a / button click (放它们冒泡)
        const target = e.target as HTMLElement;
        if (target.closest('a, button, input, select, textarea, [data-tn-noropen]')) return;
        opts.rowClick!(row);
      });
    }
    for (const col of opts.columns) {
      const td = document.createElement('td');
      td.className = `px-3 py-1.5 text-${col.align || 'left'} text-gray-800`;
      const rawVal = (row as any)[col.key];
      let v: string | HTMLElement = col.render ? col.render(row) : String(rawVal ?? '');

      // v353 · ID 缩展 (idShort=true 且未自定义 render)
      if (col.idShort && !col.render && typeof rawVal === 'string') {
        const short = shortenId(rawVal);
        v = `<span class="tn-id-short" title="完整: ${escapeHtml(rawVal)} (双击复制)" data-full="${escapeHtml(rawVal)}" style="font-family:ui-monospace,monospace;cursor:help">${escapeHtml(short)}</span>`;
        td.dataset.tnIdShort = '1';
      }

      if (v instanceof HTMLElement) td.appendChild(v);
      else td.innerHTML = v;
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);

  // v353 · 双击 ID 缩展复制
  table.addEventListener('dblclick', (e) => {
    const span = (e.target as HTMLElement).closest<HTMLElement>('.tn-id-short');
    if (!span) return;
    const full = span.dataset.full || span.textContent || '';
    void copyToClipboard(full).then(() => toast.success(`📋 已复制: ${full}`));
  });

  wrapper.appendChild(table);
  return wrapper;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch { /* fallback */ }
  // legacy fallback
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.cssText = 'position:fixed;left:-9999px';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } catch { /* */ }
  ta.remove();
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
